// Inbox Response Time Panel - Shows email response time analytics per user and over time

import { useEffect, useState } from 'react';
import { statisticsService } from '../../services/statistics';
import type { InboxResponseTimeStats } from '../../services/statistics';
import styles from './StatisticsTab.module.css';

interface Props {
  dateFrom: Date;
  dateTo: Date;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainHours = hours % 24;
  return remainHours > 0 ? `${days}d ${remainHours}h` : `${days}d`;
}

export default function InboxResponseTimePanel({ dateFrom, dateTo }: Props) {
  const [data, setData] = useState<InboxResponseTimeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('week');

  useEffect(() => {
    setLoading(true);
    setError(null);
    statisticsService
      .getInboxResponseTimes(dateFrom, dateTo, granularity)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, granularity]);

  if (loading) return <div className={styles.loading}>Loading inbox response times...</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;

  const maxTrendVal = Math.max(...data.trend.map((t) => t.avg_response_minutes), 1);

  return (
    <>
      {/* KPI Summary */}
      <div className={styles.kpiGrid}>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Avg Response Time</span>
          <span className={styles.kpiValue}>
            {data.avg_response_minutes != null ? formatDuration(data.avg_response_minutes) : 'N/A'}
          </span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Median Response Time</span>
          <span className={styles.kpiValue}>
            {data.median_response_minutes != null
              ? formatDuration(data.median_response_minutes)
              : 'N/A'}
          </span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Emails Replied To</span>
          <span className={styles.kpiValue}>{data.total_replies_tracked}</span>
        </div>
      </div>

      {/* Per-User Breakdown */}
      {data.by_user.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Response Time by Staff Member</div>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th className={styles.numericCell}>Replies</th>
                <th className={styles.numericCell}>Avg</th>
                <th className={styles.numericCell}>Median</th>
                <th className={styles.numericCell}>Fastest</th>
                <th className={styles.numericCell}>Slowest</th>
              </tr>
            </thead>
            <tbody>
              {data.by_user.map((user) => (
                <tr key={user.user_id}>
                  <td>{user.user_name}</td>
                  <td className={styles.numericCell}>{user.reply_count}</td>
                  <td className={styles.numericCell}>
                    {formatDuration(user.avg_response_minutes)}
                  </td>
                  <td className={styles.numericCell}>
                    {formatDuration(user.median_response_minutes)}
                  </td>
                  <td className={styles.numericCell}>
                    {formatDuration(user.fastest_minutes)}
                  </td>
                  <td className={styles.numericCell}>
                    {formatDuration(user.slowest_minutes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Trend Chart */}
      {data.trend.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            Response Time Trend
            <span style={{ marginLeft: '1rem' }}>
              {(['day', 'week', 'month'] as const).map((g) => (
                <button
                  key={g}
                  onClick={() => setGranularity(g)}
                  style={{
                    marginLeft: '0.25rem',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    background: granularity === g ? '#3b82f6' : 'white',
                    color: granularity === g ? 'white' : '#64748b',
                    cursor: 'pointer',
                  }}
                >
                  {g.charAt(0).toUpperCase() + g.slice(1)}
                </button>
              ))}
            </span>
          </div>
          <div className={styles.trendList}>
            {data.trend.map((point) => (
              <div key={point.period} className={styles.trendRow}>
                <span className={styles.trendPeriod}>{point.period}</span>
                <div className={styles.trendBar}>
                  <div
                    className={styles.trendBarFill}
                    style={{
                      width: `${(point.avg_response_minutes / maxTrendVal) * 100}%`,
                    }}
                  />
                </div>
                <span className={styles.trendValue}>
                  {formatDuration(point.avg_response_minutes)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.total_replies_tracked === 0 && (
        <div className={styles.section}>
          <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>
            No reply data found for this period. Response times are tracked when
            staff reply to emails through the portal inbox.
          </p>
        </div>
      )}
    </>
  );
}
